import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { MarkAsHiredModal } from '../components/hr-pipeline';
import { hrPipelineApi, jobsApi } from '../lib/api';
import { usePageVisitLogger } from '../hooks/useActivityLogger';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Share2, CheckCircle, XCircle, Phone, Pause, Calendar, 
  Clock, Users, TrendingUp, Settings, Loader2, ExternalLink,
  RefreshCw, Filter, Layers, Smartphone, Copy, Award, Zap,
  BarChart3, Timer, Star, Medal, Target, LineChart, ArrowUp, ArrowDown,
  Trophy, Search, Building2, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HRPipelinePage() {
  // Activity logging
  usePageVisitLogger('HR Pipeline');
  
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'timeline', 'analytics', 'performance', or 'ai_search_logs'
  const [stats, setStats] = useState(null);
  const [shares, setShares] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Reviewer Performance state
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  
  // Time Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('daily');
  const [analyticsDays, setAnalyticsDays] = useState(30);
  
  // Candidate Timeline state
  const [timelineData, setTimelineData] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineStatusFilter, setTimelineStatusFilter] = useState('all');
  const [timelineHrFilter, setTimelineHrFilter] = useState('all');
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  
  // Admin Status Change state
  const [statusChangeModal, setStatusChangeModal] = useState(null);
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Mark as Hired state
  const [hiredModal, setHiredModal] = useState(null);
  const [hiredCompany, setHiredCompany] = useState('');
  const [hiredDate, setHiredDate] = useState('');
  const [hiredNotes, setHiredNotes] = useState('');
  const [markingHired, setMarkingHired] = useState(false);
  
  // Swipe session modal
  const [swipeModalOpen, setSwipeModalOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJobForSwipe, setSelectedJobForSwipe] = useState('');
  const [swipeHrEmail, setSwipeHrEmail] = useState('');
  const [swipeMinScore, setSwipeMinScore] = useState('60');
  const [creatingSwipe, setCreatingSwipe] = useState(false);
  const [swipeResult, setSwipeResult] = useState(null);
  
  // AI Job Search Logs state
  const [aiSearchLogs, setAiSearchLogs] = useState(null);
  const [loadingAiSearchLogs, setLoadingAiSearchLogs] = useState(false);
  const [aiSearchLogsDays, setAiSearchLogsDays] = useState(30);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  useEffect(() => {
    if (activeTab === 'performance' && !performanceData) {
      loadPerformanceData();
    }
    if (activeTab === 'analytics') {
      loadAnalyticsData();
    }
    if (activeTab === 'timeline') {
      loadTimelineData();
    }
    if (activeTab === 'ai_search_logs') {
      loadAiSearchLogs();
    }
  }, [activeTab, analyticsPeriod, analyticsDays, timelineStatusFilter, timelineHrFilter, aiSearchLogsDays]);

  const loadAiSearchLogs = async () => {
    setLoadingAiSearchLogs(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/ai-job-search/logs?days=${aiSearchLogsDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAiSearchLogs(data);
      } else {
        toast.error('Failed to load AI search logs');
      }
    } catch (err) {
      console.error('Error loading AI search logs:', err);
      toast.error('Failed to load AI search logs');
    } finally {
      setLoadingAiSearchLogs(false);
    }
  };

  const loadTimelineData = async () => {
    setLoadingTimeline(true);
    try {
      const params = { days: 90 };
      if (timelineStatusFilter !== 'all') params.status = timelineStatusFilter;
      if (timelineHrFilter !== 'all') params.hr_email = timelineHrFilter;
      const res = await hrPipelineApi.getCandidateTimeline(params);
      setTimelineData(res.data);
    } catch (err) {
      toast.error('Failed to load timeline data');
    } finally {
      setLoadingTimeline(false);
    }
  };

  const loadAnalyticsData = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await hrPipelineApi.getTimeAnalytics(analyticsPeriod, analyticsDays);
      setAnalyticsData(res.data);
    } catch (err) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleAdminStatusChange = async (shareId, newStatus) => {
    setChangingStatus(true);
    try {
      await hrPipelineApi.adminChangeStatus(shareId, newStatus, statusChangeReason);
      toast.success(`Status changed to ${newStatus.replace('_', ' ')}. HR has been notified.`);
      setStatusChangeModal(null);
      setStatusChangeReason('');
      loadTimelineData();
    } catch (err) {
      toast.error('Failed to change status');
    } finally {
      setChangingStatus(false);
    }
  };

  // handleMarkHired is now handled inline in the MarkAsHiredModal component

  const loadPerformanceData = async () => {
    setLoadingPerformance(true);
    try {
      const res = await hrPipelineApi.getReviewerPerformance();
      setPerformanceData(res.data);
    } catch (err) {
      toast.error('Failed to load performance data');
    } finally {
      setLoadingPerformance(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, sharesRes, settingsRes] = await Promise.all([
        hrPipelineApi.getStats(),
        hrPipelineApi.listShares({ status: statusFilter === 'all' ? undefined : statusFilter, limit: 50 }),
        hrPipelineApi.getSettings()
      ]);
      setStats(statsRes.data);
      setShares(sharesRes.data.shares);
      setSettings(settingsRes.data);
    } catch (err) {
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await hrPipelineApi.updateSettings(settings);
      toast.success('Settings saved');
      setShowSettings(false);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const openSwipeModal = async () => {
    setSwipeModalOpen(true);
    setSwipeResult(null);
    try {
      const res = await jobsApi.list({ status: 'open', limit: 100 });
      setJobs(res.data.jobs || []);
    } catch (err) {
      toast.error('Failed to load jobs');
    }
  };

  const createSwipeSession = async () => {
    if (!selectedJobForSwipe || !swipeHrEmail) {
      toast.error('Please select a job and enter HR email');
      return;
    }
    
    setCreatingSwipe(true);
    try {
      const formData = new FormData();
      formData.append('job_id', selectedJobForSwipe);
      formData.append('hr_email', swipeHrEmail);
      formData.append('min_score', swipeMinScore);
      
      const res = await fetch(`${API_URL}/api/hr-pipeline/create-swipe-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create session');
      }
      
      const result = await res.json();
      setSwipeResult(result);
      toast.success(`Swipe session created with ${result.candidates_count} candidates!`);
    } catch (err) {
      toast.error(err.message || 'Failed to create swipe session');
    } finally {
      setCreatingSwipe(false);
    }
  };

  const copySwipeLink = () => {
    navigator.clipboard.writeText(swipeResult.swipe_link);
    toast.success('Link copied!');
  };

  const statusConfig = {
    pending: { icon: Clock, color: 'bg-slate-100 text-slate-700', label: 'Pending' },
    shortlisted: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Shortlisted' },
    rejected: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Rejected' },
    info_requested: { icon: Phone, color: 'bg-blue-100 text-blue-700', label: 'Info Requested' },
    on_hold: { icon: Pause, color: 'bg-amber-100 text-amber-700', label: 'On Hold' }
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} font-medium`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <Header title="HR Pipeline" subtitle="Track candidate shares and interview scheduling">
        <Button size="sm" onClick={openSwipeModal} className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600">
          <Smartphone className="w-4 h-4 mr-2" />
          Swipe Review
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)}>
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </Header>

      <div className="p-6 space-y-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'overview' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Layers className="w-4 h-4" />
            Pipeline Overview
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'timeline' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="timeline-tab"
          >
            <Clock className="w-4 h-4" />
            Candidate Journey
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'analytics' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="analytics-tab"
          >
            <LineChart className="w-4 h-4" />
            Trends & Analytics
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'performance' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Award className="w-4 h-4" />
            HR Performance
          </button>
          <button
            onClick={() => setActiveTab('ai_search_logs')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'ai_search_logs' 
                ? 'bg-blue-600 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="ai-search-logs-tab"
          >
            <Search className="w-4 h-4" />
            AI Job Search Logs
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Pipeline Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-slate-600 block mb-1">Match Threshold (%)</label>
                  <Input
                    type="number"
                    value={settings?.match_threshold || 60}
                    onChange={(e) => setSettings({ ...settings, match_threshold: parseInt(e.target.value) })}
                    min={0}
                    max={100}
                  />
                  <p className="text-xs text-slate-500 mt-1">Min match score to trigger share</p>
                </div>
                <div>
                  <label className="text-sm text-slate-600 block mb-1">Email Mode</label>
                  <Select 
                    value={settings?.email_mode || 'both'} 
                    onValueChange={(v) => setSettings({ ...settings, email_mode: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">Instant Only</SelectItem>
                      <SelectItem value="digest">Daily Digest Only</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-slate-600 block mb-1">Link Validity (hours)</label>
                  <Input
                    type="number"
                    value={settings?.link_validity_hours || 24}
                    onChange={(e) => setSettings({ ...settings, link_validity_hours: parseInt(e.target.value) })}
                    min={1}
                    max={168}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings?.auto_send || false}
                    onCheckedChange={(v) => setSettings({ ...settings, auto_send: v })}
                  />
                  <span className="text-sm text-slate-600">Auto-send to HR (without approval)</span>
                </div>
                <Button onClick={saveSettings} disabled={savingSettings}>
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Share2 className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats?.total_shared || 0}</p>
              <p className="text-xs text-slate-500">Total Shared</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats?.pending || 0}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats?.shortlisted || 0}</p>
              <p className="text-xs text-slate-500">Shortlisted</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats?.rejected || 0}</p>
              <p className="text-xs text-slate-500">Rejected</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Phone className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats?.info_requested || 0}</p>
              <p className="text-xs text-slate-500">Info Requested</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats?.interviews_confirmed || 0}</p>
              <p className="text-xs text-slate-500">Interviews</p>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Funnel */}
        <Card data-testid="hr-funnel-analytics">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const total = stats?.total_shared || 0;
              const shortlisted = stats?.shortlisted || 0;
              const interviews = stats?.interviews_confirmed || 0;
              const rejected = stats?.rejected || 0;
              const pending = stats?.pending || 0;
              
              const shortlistRate = total > 0 ? ((shortlisted / total) * 100).toFixed(1) : 0;
              const interviewRate = shortlisted > 0 ? ((interviews / shortlisted) * 100).toFixed(1) : 0;
              const overallRate = total > 0 ? ((interviews / total) * 100).toFixed(1) : 0;
              
              const funnelStages = [
                { label: 'Shared', count: total, color: 'bg-slate-600', width: '100%' },
                { label: 'Shortlisted', count: shortlisted, color: 'bg-green-500', width: `${Math.max(20, total > 0 ? (shortlisted / total) * 100 : 0)}%` },
                { label: 'Interviewed', count: interviews, color: 'bg-blue-500', width: `${Math.max(15, total > 0 ? (interviews / total) * 100 : 0)}%` },
              ];
              
              return (
                <div className="space-y-4">
                  {/* Funnel Visualization */}
                  <div className="flex flex-col items-center space-y-1">
                    {funnelStages.map((stage, index) => (
                      <div key={stage.label} className="w-full max-w-xl">
                        <div 
                          className={`${stage.color} text-white py-3 px-4 rounded-lg transition-all duration-500 flex justify-between items-center mx-auto`}
                          style={{ width: stage.width }}
                        >
                          <span className="font-medium text-sm">{stage.label}</span>
                          <span className="font-bold">{stage.count}</span>
                        </div>
                        {index < funnelStages.length - 1 && (
                          <div className="flex justify-center py-1">
                            <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-slate-300" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Conversion Metrics */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{shortlistRate}%</p>
                      <p className="text-xs text-green-600">Shortlist Rate</p>
                      <p className="text-xs text-slate-500 mt-1">Shared → Shortlisted</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{interviewRate}%</p>
                      <p className="text-xs text-blue-600">Interview Rate</p>
                      <p className="text-xs text-slate-500 mt-1">Shortlisted → Interviewed</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-700">{overallRate}%</p>
                      <p className="text-xs text-purple-600">Overall Conversion</p>
                      <p className="text-xs text-slate-500 mt-1">Shared → Interviewed</p>
                    </div>
                  </div>
                  
                  {/* Status Breakdown */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t">
                    <div className="flex items-center gap-2 p-2 rounded bg-amber-50">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <div>
                        <p className="text-sm font-medium text-amber-700">{pending}</p>
                        <p className="text-xs text-slate-500">Pending Review</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-green-50">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <div>
                        <p className="text-sm font-medium text-green-700">{shortlisted}</p>
                        <p className="text-xs text-slate-500">Shortlisted</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-red-50">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div>
                        <p className="text-sm font-medium text-red-700">{rejected}</p>
                        <p className="text-xs text-slate-500">Rejected</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 rounded bg-blue-50">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <div>
                        <p className="text-sm font-medium text-blue-700">{interviews}</p>
                        <p className="text-xs text-slate-500">Interviewed</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="shortlisted">Shortlisted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="info_requested">Info Requested</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Shares List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Shares</CardTitle>
          </CardHeader>
          <CardContent>
            {shares.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Share2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No shares yet</p>
                <p className="text-sm">Share candidates from the Matches page</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shares.map((share) => (
                  <div 
                    key={share.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-800 truncate">
                          {share.candidate_name}
                        </span>
                        {getStatusBadge(share.status)}
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {share.job_title} • Sent to {share.hr_email}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(share.created_at).toLocaleDateString()} at {new Date(share.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(`/hr-review/${share.token}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {/* Candidate Journey Timeline Tab */}
        {activeTab === 'timeline' && (
          <div className="space-y-6" data-testid="timeline-content">
            {loadingTimeline ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Status:</span>
                    <Select value={timelineStatusFilter} onValueChange={setTimelineStatusFilter}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="shortlisted">Shortlisted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                        <SelectItem value="hired">Hired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">HR Contact:</span>
                    <Select value={timelineHrFilter} onValueChange={setTimelineHrFilter}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All HR Contacts</SelectItem>
                        {timelineData?.hr_contacts?.map((hr) => (
                          <SelectItem key={hr.email} value={hr.email}>
                            {hr.name || hr.email} ({hr.count})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadTimelineData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {/* Summary Stats */}
                {timelineData?.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-slate-800">{timelineData.summary.total}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-amber-600">{timelineData.summary.pending}</p>
                      <p className="text-xs text-slate-500">Pending</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-green-600">{timelineData.summary.shortlisted}</p>
                      <p className="text-xs text-slate-500">Shortlisted</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-red-600">{timelineData.summary.rejected}</p>
                      <p className="text-xs text-slate-500">Rejected</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-purple-600">{timelineData.summary.on_hold}</p>
                      <p className="text-xs text-slate-500">On Hold</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-blue-600">{timelineData.summary.avg_days_in_pipeline}</p>
                      <p className="text-xs text-slate-500">Avg Days</p>
                    </div>
                  </div>
                )}

                {/* Candidate Timeline Cards */}
                <div className="space-y-4">
                  {timelineData?.candidates?.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No candidates in pipeline</p>
                      <p className="text-sm mt-1">Share candidates from the Matches page to see their journey here</p>
                    </div>
                  ) : (
                    timelineData?.candidates?.map((candidate) => (
                      <Card key={candidate.id} className={`overflow-hidden transition-all ${expandedCandidate === candidate.id ? 'ring-2 ring-blue-500' : ''}`}>
                        <div 
                          className="p-4 cursor-pointer hover:bg-slate-50"
                          onClick={() => setExpandedCandidate(expandedCandidate === candidate.id ? null : candidate.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {/* Status indicator */}
                              <div className={`w-3 h-3 rounded-full ${
                                candidate.current_status === 'shortlisted' ? 'bg-green-500' :
                                candidate.current_status === 'rejected' ? 'bg-red-500' :
                                candidate.current_status === 'on_hold' ? 'bg-amber-500' :
                                'bg-slate-400'
                              }`} />
                              
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-800">{candidate.candidate_name}</span>
                                  {candidate.match_score && (
                                    <Badge variant="outline" className="text-xs">
                                      {candidate.match_score}% match
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500">
                                  {candidate.job_title} {candidate.company_name && `• ${candidate.company_name}`}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm text-slate-600">Shared with {candidate.hr_name || 'HR'}</p>
                                <p className="text-xs text-slate-400">{candidate.days_in_pipeline} days in pipeline</p>
                              </div>
                              <Badge className={`${
                                candidate.current_status === 'shortlisted' ? 'bg-green-100 text-green-700' :
                                candidate.current_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                candidate.current_status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {candidate.current_status?.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Timeline View */}
                        {expandedCandidate === candidate.id && (
                          <div className="border-t bg-slate-50 p-4">
                            <div className="flex items-center gap-2 mb-4">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className="text-sm font-medium text-slate-700">Journey Timeline</span>
                            </div>
                            
                            <div className="relative pl-6">
                              {/* Timeline line */}
                              <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-slate-200" />
                              
                              {/* Timeline events */}
                              <div className="space-y-4">
                                {candidate.timeline?.map((event, idx) => (
                                  <div key={idx} className="relative flex items-start gap-3">
                                    {/* Event dot */}
                                    <div className={`absolute -left-4 mt-1.5 w-3 h-3 rounded-full border-2 border-white ${
                                      event.color === 'green' ? 'bg-green-500' :
                                      event.color === 'red' ? 'bg-red-500' :
                                      event.color === 'amber' ? 'bg-amber-500' :
                                      event.color === 'blue' ? 'bg-blue-500' :
                                      event.color === 'indigo' ? 'bg-indigo-500' :
                                      event.color === 'purple' ? 'bg-purple-500' :
                                      event.color === 'emerald' ? 'bg-emerald-500' :
                                      'bg-slate-400'
                                    }`} />
                                    
                                    <div className="flex-1 bg-white rounded-lg p-3 shadow-sm">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-slate-800">{event.label}</span>
                                        <span className="text-xs text-slate-400">
                                          {event.timestamp ? new Date(event.timestamp).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                          }) : ''}
                                        </span>
                                      </div>
                                      {event.details && (
                                        <p className="text-sm text-slate-500">{event.details}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* View Stats */}
                            {candidate.view_stats && candidate.view_stats.total_views > 0 && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <Target className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm font-medium text-blue-800">View Statistics</span>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-center">
                                  <div>
                                    <p className="text-lg font-bold text-blue-600">{candidate.view_stats.total_views}</p>
                                    <p className="text-xs text-blue-600">Total Views</p>
                                  </div>
                                  <div>
                                    <p className="text-lg font-bold text-green-600">{candidate.view_stats.hr_views}</p>
                                    <p className="text-xs text-green-600">HR Views</p>
                                  </div>
                                  <div>
                                    <p className="text-lg font-bold text-slate-600">{candidate.view_stats.admin_views}</p>
                                    <p className="text-xs text-slate-600">Admin Views</p>
                                  </div>
                                </div>
                                {candidate.view_stats.view_history?.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-blue-200">
                                    <p className="text-xs text-blue-700 mb-1">Recent Views:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {candidate.view_stats.view_history.slice(0, 5).map((v, i) => (
                                        <span key={i} className="text-xs bg-white px-2 py-0.5 rounded text-blue-700">
                                          {v.date} ({v.viewer_type})
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Quick Actions */}
                            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/ats/candidates/${candidate.candidate_id}`);
                                }}
                              >
                                View Candidate
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/hr-review/${candidate.token}`, '_blank');
                                }}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                HR Review Link
                              </Button>
                              
                              {/* Admin Status Change Dropdown */}
                              <div className="flex-1" />
                              <Select 
                                value=""
                                onValueChange={(value) => {
                                  if (value === 'hired') {
                                    setHiredModal(candidate);
                                  } else if (value) {
                                    setStatusChangeModal({ candidate, newStatus: value });
                                  }
                                }}
                              >
                                <SelectTrigger className="w-40 h-8 text-xs">
                                  <SelectValue placeholder="Change Status..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Set Pending</SelectItem>
                                  <SelectItem value="shortlisted">Shortlist</SelectItem>
                                  <SelectItem value="rejected">Reject</SelectItem>
                                  <SelectItem value="on_hold">Put On Hold</SelectItem>
                                  <SelectItem value="hired">🏆 Mark as Hired</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Reminder Opt-out Info */}
                            {candidate.current_status === 'on_hold' && (
                              <div className="mt-2 text-xs text-slate-500">
                                {candidate.reminder_opt_out 
                                  ? '⛔ HR has opted out of reminders'
                                  : `📬 Reminders sent: ${candidate.on_hold_reminder_count || 0}/3`
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    ))
                  )}
                </div>

                {/* Admin Status Change Modal */}
                {statusChangeModal && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setStatusChangeModal(null)}>
                    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
                      <h3 className="text-lg font-semibold text-slate-800 mb-4">
                        Change Status to "{statusChangeModal.newStatus.replace('_', ' ')}"
                      </h3>
                      <p className="text-sm text-slate-600 mb-4">
                        <strong>Candidate:</strong> {statusChangeModal.candidate.candidate_name}<br/>
                        <strong>Current Status:</strong> {statusChangeModal.candidate.current_status?.replace('_', ' ')}<br/>
                        <strong>HR:</strong> {statusChangeModal.candidate.hr_name || statusChangeModal.candidate.hr_email}
                      </p>
                      <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg mb-4">
                        ⚠️ The HR ({statusChangeModal.candidate.hr_email}) will be notified about this change and given an option to reopen the candidate.
                      </p>
                      <textarea
                        className="w-full border rounded-lg p-3 text-sm mb-4"
                        rows={3}
                        placeholder="Reason for status change (optional)"
                        value={statusChangeReason}
                        onChange={(e) => setStatusChangeReason(e.target.value)}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setStatusChangeModal(null)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={() => handleAdminStatusChange(statusChangeModal.candidate.id, statusChangeModal.newStatus)}
                          disabled={changingStatus}
                          className={statusChangeModal.newStatus === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
                        >
                          {changingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Confirm Change
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mark as Hired Modal */}
                {hiredModal && (
                  <MarkAsHiredModal
                    candidate={hiredModal}
                    onClose={() => setHiredModal(null)}
                    onConfirm={async (data) => {
                      setMarkingHired(true);
                      try {
                        await hrPipelineApi.markHired(hiredModal.id, data);
                        toast.success(`🏆 ${hiredModal.candidate_name} marked as hired at ${data.hired_company}!`);
                        setHiredModal(null);
                        setHiredCompany('');
                        setHiredDate('');
                        setHiredNotes('');
                        loadShares();
                      } catch (error) {
                        toast.error('Failed to mark as hired');
                      } finally {
                        setMarkingHired(false);
                      }
                    }}
                    loading={markingHired}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6" data-testid="analytics-content">
            {loadingAnalytics ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                {/* Period Controls */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">View:</span>
                    <Select value={analyticsPeriod} onValueChange={setAnalyticsPeriod}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Period:</span>
                    <Select value={String(analyticsDays)} onValueChange={(v) => setAnalyticsDays(Number(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="14">Last 14 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="60">Last 60 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadAnalyticsData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {/* Summary Cards */}
                {analyticsData?.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="border-l-4 border-l-slate-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 mb-1">Total Shared</p>
                        <p className="text-2xl font-bold text-slate-800">{analyticsData.summary.total_shared}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          ~{analyticsData.summary.avg_daily_shares}/day avg
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 mb-1">Shortlisted</p>
                        <p className="text-2xl font-bold text-green-600">{analyticsData.summary.total_shortlisted}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <ArrowUp className="w-3 h-3 text-green-500" />
                          <p className="text-xs text-green-600">{analyticsData.summary.conversion_rate}% rate</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 mb-1">Rejected</p>
                        <p className="text-2xl font-bold text-red-600">{analyticsData.summary.total_rejected}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 mb-1">Interviews</p>
                        <p className="text-2xl font-bold text-blue-600">{analyticsData.summary.total_interviews}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 mb-1">Conversion Rate</p>
                        <p className="text-2xl font-bold text-purple-600">{analyticsData.summary.conversion_rate}%</p>
                        <p className="text-xs text-slate-400 mt-1">Share → Shortlist</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Trend Chart */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <LineChart className="w-4 h-4 text-blue-500" />
                      Activity Trends ({analyticsPeriod === 'daily' ? 'Daily' : analyticsPeriod === 'weekly' ? 'Weekly' : 'Monthly'})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData?.time_series?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Simple Bar Chart Visualization */}
                        <div className="overflow-x-auto">
                          <div className="min-w-full" style={{ minWidth: Math.max(analyticsData.time_series.length * 50, 400) }}>
                            {/* Chart Area */}
                            <div className="flex items-end gap-1 h-48 border-b border-l border-slate-200 p-4">
                              {analyticsData.time_series.map((item, index) => {
                                const maxShared = Math.max(...analyticsData.time_series.map(d => d.total_shared), 1);
                                const height = (item.total_shared / maxShared) * 100;
                                const shortlistHeight = (item.shortlisted / maxShared) * 100;
                                
                                return (
                                  <div key={index} className="flex-1 flex flex-col items-center justify-end gap-0.5 min-w-[40px]" title={`${item.date}: ${item.total_shared} shared, ${item.shortlisted} shortlisted`}>
                                    <div className="w-full flex gap-0.5 items-end justify-center">
                                      <div 
                                        className="bg-slate-400 rounded-t w-3 transition-all duration-300 hover:bg-slate-500"
                                        style={{ height: `${height}%`, minHeight: item.total_shared > 0 ? '4px' : '0' }}
                                      />
                                      <div 
                                        className="bg-green-500 rounded-t w-3 transition-all duration-300 hover:bg-green-600"
                                        style={{ height: `${shortlistHeight}%`, minHeight: item.shortlisted > 0 ? '4px' : '0' }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* X-axis Labels */}
                            <div className="flex gap-1 mt-2">
                              {analyticsData.time_series.map((item, index) => (
                                <div key={index} className="flex-1 min-w-[40px] text-center">
                                  <span className="text-xs text-slate-500 transform -rotate-45 inline-block">
                                    {analyticsPeriod === 'daily' 
                                      ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                      : item.date
                                    }
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        {/* Legend */}
                        <div className="flex items-center gap-6 justify-center pt-4 border-t">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-slate-400 rounded" />
                            <span className="text-sm text-slate-600">Shared</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-500 rounded" />
                            <span className="text-sm text-slate-600">Shortlisted</span>
                          </div>
                        </div>

                        {/* Data Table */}
                        <div className="mt-6 border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="text-left p-3 font-medium text-slate-700">Date</th>
                                <th className="text-right p-3 font-medium text-slate-700">Shared</th>
                                <th className="text-right p-3 font-medium text-slate-700">Shortlisted</th>
                                <th className="text-right p-3 font-medium text-slate-700">Rejected</th>
                                <th className="text-right p-3 font-medium text-slate-700">Pending</th>
                                <th className="text-right p-3 font-medium text-slate-700">Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analyticsData.time_series.slice().reverse().slice(0, 10).map((item, index) => (
                                <tr key={index} className="border-t hover:bg-slate-50">
                                  <td className="p-3 text-slate-800">
                                    {analyticsPeriod === 'daily' 
                                      ? new Date(item.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                                      : item.date
                                    }
                                  </td>
                                  <td className="p-3 text-right font-medium">{item.total_shared}</td>
                                  <td className="p-3 text-right text-green-600">{item.shortlisted}</td>
                                  <td className="p-3 text-right text-red-600">{item.rejected}</td>
                                  <td className="p-3 text-right text-amber-600">{item.pending}</td>
                                  <td className="p-3 text-right">
                                    {item.total_shared > 0 
                                      ? <span className={`font-medium ${(item.shortlisted / item.total_shared * 100) >= 50 ? 'text-green-600' : 'text-slate-600'}`}>
                                          {((item.shortlisted / item.total_shared) * 100).toFixed(0)}%
                                        </span>
                                      : <span className="text-slate-400">-</span>
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No data available for selected period</p>
                        <p className="text-sm mt-1">Share candidates to see analytics here</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* HR Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {loadingPerformance ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : performanceData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="border border-slate-200 rounded-2xl p-4 text-center">
                    <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{performanceData.summary?.total_hr_contacts || 0}</p>
                    <p className="text-xs text-slate-500">HR Contacts</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center">
                    <Target className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{performanceData.summary?.overall_response_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Avg Response Rate</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{performanceData.summary?.overall_shortlist_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Avg Shortlist Rate</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center">
                    <Calendar className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{performanceData.summary?.total_interviews || 0}</p>
                    <p className="text-xs text-slate-500">Total Interviews</p>
                  </div>
                </div>

                {/* Top Performers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-slate-200 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 rounded-lg bg-green-100">
                        <Zap className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">Best Response Rate</span>
                    </div>
                    {performanceData.top_performers?.best_response_rate?.hr_name ? (
                      <div>
                        <p className="text-lg font-bold text-slate-800">{performanceData.top_performers.best_response_rate.hr_name}</p>
                        <p className="text-2xl font-bold text-green-600">{performanceData.top_performers.best_response_rate.value}%</p>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">No data yet</p>
                    )}
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Timer className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">Fastest Responder</span>
                    </div>
                    {performanceData.top_performers?.fastest_responder?.hr_name ? (
                      <div>
                        <p className="text-lg font-bold text-slate-800">{performanceData.top_performers.fastest_responder.hr_name}</p>
                        <p className="text-2xl font-bold text-blue-600">{performanceData.top_performers.fastest_responder.value}h avg</p>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">No data yet</p>
                    )}
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <Star className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">Best Shortlist Rate</span>
                    </div>
                    {performanceData.top_performers?.best_shortlist_rate?.hr_name ? (
                      <div>
                        <p className="text-lg font-bold text-slate-800">{performanceData.top_performers.best_shortlist_rate.hr_name}</p>
                        <p className="text-2xl font-bold text-amber-600">{performanceData.top_performers.best_shortlist_rate.value}%</p>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-sm">No data yet</p>
                    )}
                  </div>
                </div>

                {/* Reviewers Table */}
                <div className="border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Medal className="w-4 h-4 text-slate-600" />
                      HR Reviewer Leaderboard
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">HR Contact</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Shared</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Response Rate</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Avg Response</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Shortlisted</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Shortlist Rate</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Interviews</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {performanceData.reviewers?.map((reviewer, idx) => (
                          <tr key={reviewer.hr_email} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                  idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                                }`}>
                                  {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-slate-800">{reviewer.hr_name}</p>
                                  <p className="text-xs text-slate-500">{reviewer.hr_email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="font-semibold text-slate-800">{reviewer.total_shared}</span>
                              <span className="text-xs text-slate-400 ml-1">({reviewer.pending} pending)</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      reviewer.response_rate >= 80 ? 'bg-green-500' : 
                                      reviewer.response_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${reviewer.response_rate}%` }}
                                  />
                                </div>
                                <span className={`font-semibold text-sm ${
                                  reviewer.response_rate >= 80 ? 'text-green-600' : 
                                  reviewer.response_rate >= 50 ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                  {reviewer.response_rate}%
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {reviewer.avg_response_hours ? (
                                <span className={`font-semibold ${
                                  reviewer.avg_response_hours <= 24 ? 'text-green-600' : 
                                  reviewer.avg_response_hours <= 72 ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                  {reviewer.avg_response_hours < 24 
                                    ? `${reviewer.avg_response_hours}h` 
                                    : `${Math.round(reviewer.avg_response_hours / 24)}d`
                                  }
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="font-semibold text-green-600">{reviewer.shortlisted}</span>
                              <span className="text-xs text-slate-400">/{reviewer.reviewed}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-semibold ${
                                reviewer.shortlist_rate >= 50 ? 'text-green-600' : 
                                reviewer.shortlist_rate >= 25 ? 'text-amber-600' : 'text-slate-600'
                              }`}>
                                {reviewer.shortlist_rate}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="font-semibold text-purple-600">{reviewer.interviews_confirmed}</span>
                              {reviewer.interviews_scheduled > reviewer.interviews_confirmed && (
                                <span className="text-xs text-slate-400">/{reviewer.interviews_scheduled}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {(!performanceData.reviewers || performanceData.reviewers.length === 0) && (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                              <p>No HR reviewer data yet</p>
                              <p className="text-sm">Share candidates from the Matches page to see performance metrics</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={loadPerformanceData} disabled={loadingPerformance}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingPerformance ? 'animate-spin' : ''}`} />
                    Refresh Data
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">No performance data available</p>
                <p className="text-sm">Share candidates with HR to start tracking performance</p>
              </div>
            )}
          </div>
        )}

        {/* AI Job Search Logs Tab */}
        {activeTab === 'ai_search_logs' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Period:</Label>
                  <Select value={String(aiSearchLogsDays)} onValueChange={(v) => setAiSearchLogsDays(parseInt(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button variant="outline" onClick={loadAiSearchLogs} disabled={loadingAiSearchLogs}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingAiSearchLogs ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {loadingAiSearchLogs ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : aiSearchLogs ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="border border-slate-200 rounded-2xl p-4 text-center bg-blue-50">
                    <Search className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{aiSearchLogs.summary?.total_searches || 0}</p>
                    <p className="text-xs text-slate-500">Total Searches</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center bg-green-50">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{aiSearchLogs.summary?.completed_searches || 0}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center bg-purple-50">
                    <Target className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{aiSearchLogs.summary?.total_jobs_found || 0}</p>
                    <p className="text-xs text-slate-500">Jobs Found</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center bg-amber-50">
                    <Users className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{aiSearchLogs.summary?.total_hr_contacts_found || 0}</p>
                    <p className="text-xs text-slate-500">HR Contacts Found</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center bg-teal-50">
                    <Share2 className="w-6 h-6 text-teal-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{aiSearchLogs.summary?.total_shares_sent || 0}</p>
                    <p className="text-xs text-slate-500">Shares Sent</p>
                  </div>
                  <div className="border border-slate-200 rounded-2xl p-4 text-center bg-emerald-50">
                    <Trophy className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-800">{aiSearchLogs.summary?.total_shares_accepted || 0}</p>
                    <p className="text-xs text-slate-500">Shares Accepted</p>
                  </div>
                </div>

                {/* Success Rate */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-slate-600" />
                      Search Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-8">
                      <div>
                        <p className="text-sm text-slate-500">Success Rate</p>
                        <p className="text-3xl font-bold text-green-600">{aiSearchLogs.summary?.success_rate || 0}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Unique Candidates Searched</p>
                        <p className="text-3xl font-bold text-blue-600">{aiSearchLogs.summary?.unique_candidates_searched || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Failed Searches</p>
                        <p className="text-3xl font-bold text-red-500">{aiSearchLogs.summary?.failed_searches || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Searches */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-600" />
                      Recent Searches
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Candidate</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Status</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Jobs Found</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Jobs Matched</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Queries</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Errors</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiSearchLogs.recent_searches?.map((search, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium text-slate-800">
                                {search.candidate_name}
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={
                                  search.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  search.status === 'failed' ? 'bg-red-100 text-red-700' :
                                  search.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {search.status}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-center font-semibold text-purple-600">
                                {search.jobs_found || 0}
                              </td>
                              <td className="px-3 py-2 text-center font-semibold text-green-600">
                                {search.jobs_matched || 0}
                              </td>
                              <td className="px-3 py-2 text-center text-slate-600">
                                {search.queries_used || 0}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {search.error_count > 0 ? (
                                  <span className="text-red-500 font-medium">{search.error_count}</span>
                                ) : (
                                  <span className="text-slate-400">0</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-slate-500">
                                {search.created_at ? new Date(search.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                          {(!aiSearchLogs.recent_searches || aiSearchLogs.recent_searches.length === 0) && (
                            <tr>
                              <td colSpan="7" className="px-3 py-8 text-center text-slate-500">
                                No searches recorded in this period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Candidates Summary */}
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-600" />
                      Candidate Search Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Candidate</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Total Searches</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Jobs Found</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">HR Contacts Found</th>
                            <th className="text-center px-3 py-2 text-slate-500 font-medium">Shares Sent</th>
                            <th className="text-left px-3 py-2 text-slate-500 font-medium">Last Search</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiSearchLogs.candidates?.slice(0, 10).map((candidate, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium text-slate-800">
                                {candidate.candidate_name}
                              </td>
                              <td className="px-3 py-2 text-center text-blue-600 font-semibold">
                                {candidate.search_count}
                              </td>
                              <td className="px-3 py-2 text-center text-purple-600 font-semibold">
                                {candidate.jobs_found}
                              </td>
                              <td className="px-3 py-2 text-center text-amber-600 font-semibold">
                                {candidate.hr_contacts_found}
                              </td>
                              <td className="px-3 py-2 text-center text-teal-600 font-semibold">
                                {candidate.shares_sent}
                              </td>
                              <td className="px-3 py-2 text-slate-500">
                                {candidate.last_search ? new Date(candidate.last_search).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                          {(!aiSearchLogs.candidates || aiSearchLogs.candidates.length === 0) && (
                            <tr>
                              <td colSpan="6" className="px-3 py-8 text-center text-slate-500">
                                No candidates searched in this period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Companies with HR Contacts */}
                {aiSearchLogs.top_companies?.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-600" />
                        Top Companies with HR Contacts
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {aiSearchLogs.top_companies.map((company, idx) => (
                          <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                            <p className="font-medium text-slate-800 text-sm truncate" title={company.company}>
                              {company.company}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                <Mail className="w-3 h-3 mr-1" />
                                {company.hr_contacts} HR
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Search className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">No AI search data available</p>
                <p className="text-sm">Run AI job searches to see analytics here</p>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Swipe Review Modal */}
      <Dialog open={swipeModalOpen} onOpenChange={setSwipeModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-pink-500" />
              Create Swipe Review Session
            </DialogTitle>
          </DialogHeader>
          
          {!swipeResult ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Create a mobile-friendly swipe interface for HR to quickly review candidates. Like Tinder, but for hiring!
              </p>
              
              <div>
                <Label>Select Job</Label>
                <Select value={selectedJobForSwipe} onValueChange={setSelectedJobForSwipe}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map(job => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.title} - {job.company_name || 'No company'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>HR Email</Label>
                <Input
                  type="email"
                  value={swipeHrEmail}
                  onChange={(e) => setSwipeHrEmail(e.target.value)}
                  placeholder="hr@company.com"
                />
              </div>
              
              <div>
                <Label>Minimum Match Score</Label>
                <Select value={swipeMinScore} onValueChange={setSwipeMinScore}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50%+</SelectItem>
                    <SelectItem value="60">60%+</SelectItem>
                    <SelectItem value="70">70%+</SelectItem>
                    <SelectItem value="80">80%+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={createSwipeSession} disabled={creatingSwipe} className="w-full">
                {creatingSwipe ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
                Create Swipe Session
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-pink-50 to-orange-50 rounded-lg p-4 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="font-medium text-slate-800">Session Created!</p>
                <p className="text-sm text-slate-600">{swipeResult.candidates_count} candidates to review</p>
              </div>
              
              <div className="bg-slate-100 rounded-lg p-3 flex items-center gap-2">
                <code className="text-xs flex-1 truncate text-slate-600">
                  {swipeResult.swipe_link}
                </code>
                <Button size="sm" variant="ghost" onClick={copySwipeLink}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => window.open(swipeResult.swipe_link, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Link
                </Button>
                <Button onClick={() => setSwipeModalOpen(false)}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
